/**
 * Bajar los archivos validados del caso («↓ Descargar», 19-oct-2026): el de cada
 * requisito validado —por la IA o por un ejecutivo—, con el nombre del requisito.
 * Qué entra lo decide `documentosDelCaso.descargables`.
 *
 * La URL la firma el portal (`/documents/{id}/preview?descargar=1`): la misma
 * ruta y el mismo permiso que el visor, con `attachment` y el nombre de §9.31
 * (`DNI_74055612_…pdf`), que es como el analista lo busca después. Vence a los
 * 5 minutos, así que se pide al hacer clic y nunca al cargar la lista.
 */
import apiClient from './api/client'

interface UrlDeDescarga {
  url: string
  filename: string
}

/** Pausa entre archivos: el navegador agrupa los que llegan juntos y algunos
 *  descarta. Con «Descargar validados» Chrome pregunta una sola vez si se
 *  permiten varias descargas. */
const ENTRE_ARCHIVOS_MS = 700

function bajar(url: string): void {
  // Un enlace y no `window.location`: con `attachment` el navegador baja el
  // archivo sin salir del portal, y el enlace no deja historial.
  const a = document.createElement('a')
  a.href = url
  a.rel = 'noopener'
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()
  a.remove()
}

/** `nombre`: el requisito que cumple («Copia Literal del inmueble»); el
 *  backend le pone la extensión y lo limpia. Sin él, el nombre de §9.31. */
export async function descargarDocumento(docId: string, nombre = ''): Promise<void> {
  const params = new URLSearchParams({ descargar: '1' })
  if (nombre) params.set('nombre', nombre)
  const { data } = await apiClient.get<UrlDeDescarga>(
    `/documents/${encodeURIComponent(docId)}/preview?${params.toString()}`)
  if (!data.url) throw new Error('El documento no tiene archivo asociado.')
  bajar(data.url)
}

/** Baja uno tras otro, cada uno con su nombre. Devuelve los ids que fallaron. */
export async function descargarVarios(
  archivos: ReadonlyArray<{ docId: string; nombre?: string }>,
): Promise<string[]> {
  const fallidos: string[] = []
  for (const [i, { docId, nombre }] of archivos.entries()) {
    if (i > 0) await new Promise((listo) => setTimeout(listo, ENTRE_ARCHIVOS_MS))
    try {
      await descargarDocumento(docId, nombre)
    } catch {
      fallidos.push(docId)
    }
  }
  return fallidos
}
